import { apiFetch } from './api';

/**
 * Phase 20 frontend mirror of the Phase 19 read-only execution history API.
 *
 * GET-only by construction: this module deliberately exposes no mutation
 * helpers — there is no retry, no delete, no edit, and no execution-state
 * change in the history surface. The filter vocabularies below are exactly
 * the values the backend accepts (schema CHECKs); anything else would be
 * rejected with 400, so invalid options are impossible by construction.
 */

/** Schema CHECK vocabulary of `automation_executions.status` (filter values). */
export type ExecutionStatusFilter = 'pending' | 'pending_retry' | 'success' | 'failed' | 'blocked';

/** Schema CHECK vocabulary of `automation_actions.action_type` (filter values). */
export type ExecutionActionFilter =
  | 'reply_comment'
  | 'private_reply'
  | 'send_dm_text'
  | 'send_dm_quick_replies'
  | 'send_dm_template'
  | 'advance_flow';

export const EXECUTION_STATUS_FILTERS: readonly ExecutionStatusFilter[] = [
  'pending',
  'pending_retry',
  'success',
  'failed',
  'blocked',
];

export const EXECUTION_ACTION_FILTERS: readonly ExecutionActionFilter[] = [
  'reply_comment',
  'private_reply',
  'send_dm_text',
  'send_dm_quick_replies',
  'send_dm_template',
  'advance_flow',
];

/**
 * Exactly the 12 safe fields the Phase 19 routes return — no tokens, no
 * secrets, no conversation payloads, no provider response bodies.
 * Status/action type stay `string` so an unknown future value renders with
 * a safe raw fallback instead of breaking the page.
 */
export interface ExecutionHistoryItem {
  id: string;
  automation_id: string;
  action_id: string | null;
  action_type: string | null;
  external_event_id: string;
  status: string;
  http_status: number | null;
  error_code: string | null;
  attempt: number;
  next_attempt_at: number | null;
  created_at: number;
  updated_at: number;
}

export interface ExecutionListResponse {
  executions: ExecutionHistoryItem[];
  limit: number;
  offset: number;
  total: number;
}

export interface ExecutionDetailResponse {
  execution: ExecutionHistoryItem;
}

/** Only the four filters the Phase 19 API supports. */
export interface ExecutionListQuery {
  limit: number;
  offset: number;
  automationId?: string;
  status?: string;
  actionType?: string;
  externalEventId?: string;
}

export function listExecutionsRequest(query: ExecutionListQuery): Promise<ExecutionListResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit));
  params.set('offset', String(query.offset));
  if (query.automationId !== undefined && query.automationId !== '') {
    params.set('automation_id', query.automationId);
  }
  if (query.status !== undefined && query.status !== '') {
    params.set('status', query.status);
  }
  if (query.actionType !== undefined && query.actionType !== '') {
    params.set('action_type', query.actionType);
  }
  if (query.externalEventId !== undefined && query.externalEventId !== '') {
    params.set('external_event_id', query.externalEventId);
  }
  return apiFetch<ExecutionListResponse>(`/api/automation-executions?${params.toString()}`);
}

export function getExecutionRequest(id: string): Promise<ExecutionDetailResponse> {
  return apiFetch<ExecutionDetailResponse>(`/api/automation-executions/${encodeURIComponent(id)}`);
}
